import {
  ChangeDetectionStrategy,
  Component,
  DestroyRef,
  inject,
  input,
  output,
} from '@angular/core';

import { InputTextModule } from 'primeng/inputtext';
import {
  FormBuilder,
  FormControl,
  FormGroup,
  ReactiveFormsModule,
  Validators,
} from '@angular/forms';
import {
  AlertsComponent,
  AlertsService,
  FormControlComponent,
  PrimengButtonDirective,
  PrimengPasswordDirective,
} from '@web/shared/ui';
import { Button } from 'primeng/button';
import { PasswordModule } from 'primeng/password';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { HttpErrorService } from '@web/common';
import { HttpErrorResponse } from '@angular/common/http';
import { PublicProductAuthService } from '../../services/public-product-auth.service';

type PublicProductDetailsPasswordFormGroup = FormGroup<{
  password: FormControl<string>;
}>;

@Component({
  selector: 'app-public-product-details-authorization',
  imports: [
    InputTextModule,
    ReactiveFormsModule,
    FormControlComponent,
    Button,
    PrimengButtonDirective,
    PasswordModule,
    PrimengPasswordDirective,
    AlertsComponent,
  ],
  providers: [AlertsService],
  templateUrl: './public-product-details-authorization.component.html',
  styleUrl: './public-product-details-authorization.component.scss',
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class PublicProductDetailsAuthorizationComponent {
  private readonly builder = inject(FormBuilder);
  /* prettier-ignore */
  private readonly publicProductAuthorization = inject(PublicProductAuthService);
  private readonly httpError = inject(HttpErrorService);
  private readonly alerts = inject(AlertsService);

  public readonly slug = input.required<string>();
  public readonly authorizationSuccessEvent = output();

  protected readonly form = this.buildForm();

  private readonly destroyRef = inject(DestroyRef);

  public handleSubmitClick(): void {
    const { password } = this.form.getRawValue();

    this.publicProductAuthorization
      .authorize(this.slug(), password)
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe({
        next: () => this.authorizationSuccessEvent.emit(),
        error: (response) => this.showFailureAlertByResponse(response),
      });
  }

  private buildForm(): PublicProductDetailsPasswordFormGroup {
    return this.builder.group({
      password: this.builder.nonNullable.control('', [Validators.required]),
    });
  }

  private showFailureAlertByResponse(response: HttpErrorResponse): void {
    this.alerts.showSingle((builder) =>
      builder.buildFailure(
        this.httpError.extract(response).multiLineMessages(),
      ),
    );
  }
}
